/**
 * System Admin Guard Component
 *
 * Protects pages that require the user to be in the system tenant
 * and have admin or owner role. Used for platform-level admin pages
 * like Platform Providers.
 *
 * Shows an "Access Denied" message and redirects users who:
 * - Are not in the system tenant
 * - Don't have admin/owner role in the current tenant
 */

import { Navigate } from 'react-router-dom';
import { useTenant } from '@/contexts/TenantContext';
import { useHasRole } from '@/hooks/useRoles';
import { ShieldAlert } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface SystemAdminGuardProps {
  children: React.ReactNode;
  /** Where to redirect when access is denied. Defaults to /settings */
  redirectTo?: string;
}

// =============================================================================
// Hook: useIsSystemAdmin
// =============================================================================

/**
 * Returns true if the current user has admin role in the system tenant.
 *
 * @example
 *   const isSystemAdmin = useIsSystemAdmin();
 *   if (isSystemAdmin) { ... }
 */
export function useIsSystemAdmin(): boolean {
  const { currentTenant } = useTenant();
  const isAdmin = useHasRole('admin');
  return isAdmin && currentTenant?.is_system_tenant === true;
}

// =============================================================================
// Guard Component
// =============================================================================

export function SystemAdminGuard({ children, redirectTo = '/settings' }: SystemAdminGuardProps) {
  const { currentTenant, isLoading } = useTenant();
  const isAdmin = useHasRole('admin');
  const isSystemTenantAdmin = isAdmin && currentTenant?.is_system_tenant === true;

  // While loading tenant info, show nothing (avoid flash)
  if (isLoading) {
    return null;
  }

  // Not in system tenant or not admin → access denied
  if (!isSystemTenantAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Access Denied
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            This page is only available to system tenant administrators.
          </p>
        </div>
        <Navigate to={redirectTo} replace />
      </div>
    );
  }

  return <>{children}</>;
}
